import type { BlastChannel, SessionUser } from '@motoveo/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { OperatingGuardrails } from '../components/OperatingGuardrails';
import { formatCents, formatMiles } from '../lib/format';

/**
 * Blast composer — refined model (2026-07-07): a blast is a BROADCAST to every active franchise
 * dealer with a contact (one blast_recipients row each — the moat's denominator is the whole
 * network). There is no recipient curation: ISO matches are shown ONLY as the optional
 * priority/highlight preview (which dealers get sorted/flagged first). Creating routes to the blast
 * detail, where the operator sees the MASKED preview exactly as a dealer will and fires the
 * compliant send.
 */
export function BlastComposerPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const detail = useQuery({ queryKey: ['submission', id], queryFn: () => api.submissions.get(id), enabled: Boolean(id) });
  const matches = useQuery({ queryKey: ['matches', id], queryFn: () => api.matches.forSubmission(id), enabled: Boolean(id) });
  const existing = useQuery({ queryKey: ['blasts', id], queryFn: () => api.blasts.listForSubmission(id), enabled: Boolean(id) });

  const [channel, setChannel] = useState<BlastChannel>('sms');
  const [overrideReason, setOverrideReason] = useState('');

  // M10 — the launch gate. The submission detail already carries the current readiness; a BLOCKED gate
  // requires an explicit override reason before create() will proceed (the API re-checks + audits it).
  const readiness = detail.data?.launch_readiness;
  const blocked = readiness ? !readiness.ready : false;
  const networkCount = readiness?.active_franchise_dealer_count ?? null;
  const reachableCount = readiness?.reachable_dealer_count ?? null;

  const create = useMutation({
    mutationFn: () =>
      api.blasts.create(id, {
        channel,
        launch_override_reason: blocked && overrideReason.trim() ? overrideReason.trim() : undefined,
      }),
    onSuccess: (blast) => navigate(`/blasts/${blast.blast.id}`),
  });

  return (
    <Layout user={user}>
      <p>
        <Link to={`/submissions/${id}`} className="muted">
          ← Submission
        </Link>
      </p>
      <div className="row-between">
        <h2>Compose blast {detail.data ? <span className="muted">· {detail.data.submission.public_ref}</span> : null}</h2>
        {readiness ? (
          readiness.ready ? (
            <span className="pill conf-high">launch gate: READY</span>
          ) : (
            <span className="pill conf-low">launch gate: BLOCKED</span>
          )
        ) : null}
      </div>

      <OperatingGuardrails variant="blast" />

      {/* M10 — a BLOCKED launch gate: show why + require an explicit override reason to proceed. */}
      {blocked && readiness ? (
        <div className="card warn-card">
          <h3>Launch gate blocked this car</h3>
          <p>
            {readiness.blockers.map((b) => (
              <span key={b} className="pill conf-low">
                {b}
              </span>
            ))}
          </p>
          <ul className="tight">
            {readiness.reasons.map((r, i) => (
              <li key={i} className="muted">
                {r}
              </li>
            ))}
          </ul>
          <label className="form" style={{ marginTop: 8 }}>
            <span className="hint">Override reason (recorded + audited) — a judgment call to launch anyway</span>
            <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={2} placeholder="Why launch despite the block?" />
          </label>
        </div>
      ) : null}

      {existing.data && existing.data.items.length > 0 ? (
        <div className="card">
          <h3>Existing blasts</h3>
          <ul>
            {existing.data.items.map((b) => (
              <li key={b.id}>
                <Link to={`/blasts/${b.id}`}>{b.status}</Link> · {b.recipient_count} asked · {b.bid_count} bids ·{' '}
                <span className="muted">{new Date(b.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* BROADCAST — every active franchise dealer receives this car; no curation. */}
      <div className="card">
        <div className="row-between">
          <h3>
            Broadcast to the franchise network{' '}
            {networkCount != null ? (
              <span className="muted">
                ({networkCount} active franchise dealer{networkCount === 1 ? '' : 's'}
                {reachableCount != null ? ` · ${reachableCount} reachable` : ''})
              </span>
            ) : null}
          </h3>
          <label className="inline">
            Channel
            <select value={channel} onChange={(e) => setChannel(e.target.value as BlastChannel)}>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="both">Both</option>
            </select>
          </label>
        </div>
        <p className="muted">
          Every active franchise dealer with a contact receives this car (the dealer with the right buyer self-identifies and
          bids up). ISO standing interests below only <strong>prioritize + highlight</strong> — they never limit who receives it.
        </p>

        {!detail.data ? (
          <p className="muted">Loading…</p>
        ) : !matches.data?.spec_ready ? (
          <p className="muted">Decode the vehicle first — a blast needs a resolved spec.</p>
        ) : (
          <div className="inline" style={{ marginTop: 12 }}>
            <button onClick={() => create.mutate()} disabled={create.isPending || (blocked && !overrideReason.trim())}>
              {create.isPending
                ? 'Creating…'
                : blocked
                  ? 'Override & create broadcast'
                  : `Create broadcast${networkCount != null ? ` → ${networkCount} franchise dealer(s)` : ''}`}
            </button>
            {create.isError ? <span className="error">{(create.error as Error).message}</span> : null}
          </div>
        )}
      </div>

      {/* ISO PRIORITY PREVIEW — the optional highlight signal (who gets flagged/sorted first). */}
      <div className="card">
        <h3>
          ISO priority preview{' '}
          {matches.data ? <span className="muted">({matches.data.matches.length} standing-interest hit{matches.data.matches.length === 1 ? '' : 's'})</span> : null}
        </h3>
        {matches.isLoading ? (
          <p className="muted">Matching…</p>
        ) : matches.isError ? (
          <p className="error">{(matches.error as Error).message}</p>
        ) : !matches.data?.spec_ready ? (
          <p className="muted">Decode the vehicle to preview ISO priority.</p>
        ) : matches.data.matches.length === 0 ? (
          <p className="muted">No standing interests match this spec — the broadcast still reaches the whole network.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Score</th>
                  <th>Dealer</th>
                  <th>State</th>
                  <th>ISO pref</th>
                  <th>Buyer-in-hand</th>
                  <th>Contact</th>
                  <th>Reachable</th>
                </tr>
              </thead>
              <tbody>
                {matches.data.matches.map((m) => (
                  <tr key={`${m.dealer_id}-${m.iso_pref_id}`} className={m.has_named_buyer ? 'wedge-row' : ''}>
                    <td>
                      <strong>{m.score.toFixed(1)}</strong>
                    </td>
                    <td>
                      <Link to={`/dealers/${m.dealer_id}`}>{m.dealer_business_name}</Link>
                    </td>
                    <td className="muted">{m.dealer_state}</td>
                    <td>{m.iso_pref_label}</td>
                    <td>{m.has_named_buyer ? <span className="pill conf-high">buyer waiting</span> : <span className="muted">—</span>}</td>
                    <td className="muted">{m.contact_name ?? m.contact_phone_e164 ?? m.contact_email ?? '—'}</td>
                    <td>{m.contact_reachable ? <span className="ok-dot">●</span> : <span className="warn" title="capture consent on the dealer">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail.data?.spec ? (
        <div className="card">
          <h3>Vehicle</h3>
          <p>
            <strong>{detail.data.spec.model_designation ?? '—'}</strong> · {detail.data.spec.transmission} ·{' '}
            {detail.data.spec.paint_to_sample ? `PTS ${detail.data.spec.pts_color_name ?? ''}`.trim() : detail.data.spec.exterior_color} ·{' '}
            {formatMiles(detail.data.submission.stated_mileage)} · {formatCents(detail.data.spec.total_msrp_cents)} MSRP
          </p>
        </div>
      ) : null}
    </Layout>
  );
}
